import { z } from 'zod';
import { useMyForm } from '../../../../../../react-commons/hooks/useMyForm';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    ProjectUserGroupRoleAssignmentMutationResourceSchema,
    ProjectUserGroupRoleAssignmentPropertyValidation,
    type ProjectUserGroupRoleAssignmentResource,
    useProjectUserGroupRoleAssignmentActions,
} from '../../../../resource/resources/persistables/user/projectUserGroupRoleAssignmentResource';
import type { ProjectUserRoleResource } from '../../../../resource/resources/persistables/user/projectUserRoleResource';
import { useSystemInformationActions } from '../../../../resource/services/systemInformationService';
import { Form_AutocompleteResourceTextField } from '../../../reusable-components/base/reference-text-fields/Form_AutocompleteResourceTextField';
import { EditChildBaseDialog } from '../../../reusable-components/dialogs/EditChildBaseDialog';
import { Form_DisplayComponentBaseView } from '../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { handleMutationResult } from '../../../util/utilFunctions';

export const AddOrUpdateProjectUserGroupRoleAssignmentDialog = ({
    onClose,
    projectUserRoleAssignment,
    roles,
    parentId,
    setProjectUserRoleAssignment,
}: {
    projectUserRoleAssignment: ProjectUserGroupRoleAssignmentResource;
    roles: Array<ProjectUserRoleResource>;
    onClose: () => void;
    parentId: string | undefined;
    setProjectUserRoleAssignment?: (newResource: ProjectUserGroupRoleAssignmentResource) => void;
}) => {
    const projectUserGroupRoleAssignmentActions = useProjectUserGroupRoleAssignmentActions();
    const systemInformationActions = useSystemInformationActions();

    const addRoleAssignment = projectUserGroupRoleAssignmentActions.useAddResource();
    const updateRoleAssignment = projectUserGroupRoleAssignmentActions.useUpdateResource();

    const propertyDetails = systemInformationActions.useGetSpecificPropertyDetails(
        ProjectUserGroupRoleAssignmentPropertyValidation.PROJECT_USER_ROLES,
        ResourceType.PROJECT_USER_GROUP_ROLE_ASSIGNMENT
    );

    const formSchema = ProjectUserGroupRoleAssignmentMutationResourceSchema.extend({
        selectedRole: z.string().nullable(),
    });

    const defaultValues: z.infer<typeof formSchema> = { ...projectUserRoleAssignment, selectedRole: null };

    const { form, formNames, onSubmit } = useMyForm({
        schema: formSchema,
        defaultValues,
        submitHandler: async (data) => {
            if (!data.selectedRole) {
                return;
            }
            if (projectUserRoleAssignment.id !== '') {
                const roleAssignment = { ...projectUserRoleAssignment };
                roleAssignment.projectUserRoles.push(data.selectedRole);
                await updateRoleAssignment.mutateAsync(
                    {
                        resource: roleAssignment,
                        id: roleAssignment.id,
                    },
                    {
                        onSuccess: handleMutationResult(onClose),
                    }
                );
            } else if (parentId !== undefined) {
                const roleAssignment = { ...projectUserRoleAssignment };
                roleAssignment.projectUserRoles.push(data.selectedRole);
                await addRoleAssignment.mutateAsync(
                    {
                        resource: roleAssignment,
                        parentId,
                    },
                    {
                        onSuccess: handleMutationResult(onClose),
                    }
                );
            } else if (setProjectUserRoleAssignment !== undefined) {
                const roleAssignment = { ...projectUserRoleAssignment };
                roleAssignment.projectUserRoles.push(data.selectedRole);
                setProjectUserRoleAssignment(roleAssignment);
                onClose();
            }
        },
    });

    return (
        <EditChildBaseDialog
            edited={form.formState.isDirty}
            onClose={onClose}
            title={'Add Role Assignment to Project User Group'}
            resourceType={ResourceType.PROJECT_USER_GROUP_ROLE_ASSIGNMENT}
        >
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <Form_AutocompleteResourceTextField
                    label={'Role'}
                    control={form.control}
                    name={formNames.selectedRole}
                    nullable={true}
                    readOnly={false}
                    resources={roles.filter((value) => !projectUserRoleAssignment.projectUserRoles.includes(value.id))}
                    propertyDetails={propertyDetails}
                    labelAccessor={'name'}
                    idSelector={'id'}
                />
            </Form_DisplayComponentBaseView>
        </EditChildBaseDialog>
    );
};
